import { useEffect, useState, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useGetAiOperatorConfigQuery } from '@app/api/AiOperatorConfig';
import { useLazyGetBranchesForSystemQuery } from '@app/api/Branches';
import { useUserPrint } from '@app/hooks';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import { handleCatchError } from '@app/services';

import type { BaseOptionType } from 'antd/es/select';

export const useAiSettingsTabEffect = () => {
  const { activeBranchId: ActiveBranchId } = useUserPrint();
  const { showNotification } = useNotification();

  const [searchParams] = useSearchParams();

  const aiUserId = useMemo(
    () => +(searchParams.get('id') || 0),
    [searchParams]
  );

  const [allBranches, setAllBranches] = useState<BaseOptionType[]>([]);

  const {
    data,
    isLoading: isDataLoading,
    error,
  } = useGetAiOperatorConfigQuery(
    {
      ActiveBranchId,
      userId: aiUserId,
    },
    { skip: !ActiveBranchId || !aiUserId }
  );
  const [getBranchesForSystem] = useLazyGetBranchesForSystemQuery();

  useEffect(() => {
    if (error) {
      showNotification(handleCatchError(error));
    }
  }, [error, showNotification]);

  const handleGetBranches = useCallback(async () => {
    try {
      const response = await getBranchesForSystem({ ActiveBranchId }).unwrap();
      const adaptedBranches = response.map(({ Id, Name }) => {
        return {
          value: Id,
          label: Name,
        };
      });

      setAllBranches(adaptedBranches);
    } catch (error) {
      showNotification(handleCatchError(error));
    }
  }, [ActiveBranchId, getBranchesForSystem, showNotification]);

  useEffect(() => {
    handleGetBranches();
  }, [handleGetBranches]);

  return { data, isDataLoading, allBranches };
};
